import { Injectable, inject } from '@angular/core';
import {
  Company,
  CompanyContext,
  SearchState,
  applyColumnState,
  companyColumns,
  contextFilter,
  contextQueryKey,
  deserializeSearchState,
  savedListColumns,
  toGridStoreFilter,
  toSortDescriptors,
  toTableColumns,
} from '@app/model';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { COMPANY_LIST_PAGE_SIZE, CompanyListSource } from '../data';
import { CompanyService } from './company.service';

// Built on first use: the model and core barrels import each other.
let definitions: ReturnType<typeof toTableColumns> | null = null;
const columnDefinitions = () =>
  (definitions ??= toTableColumns(companyColumns));
const dateFields = () =>
  columnDefinitions()
    .filter((column) => column.dataType === 'date' && column.field)
    .map((column) => column.field as string);

/** The request filter of a list page with the given filter value. */
export const toListRequestFilter = (filterValue: unknown[] | null) =>
  toGridStoreFilter(filterValue, dateFields());

const sameJson = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const searchSortDescriptors = (state: SearchState) =>
  state.sort
    ? [{ selector: state.sort.field, desc: state.sort.order === -1 }]
    : null;

/**
 * The company opened last from a query (list or search with filter and
 * sort): its id, its index in the whole list and the scroll offset the table
 * had when the user left it.
 */
export interface LastOpened {
  /** Null when only the scroll offset is known. */
  id: string | null;
  index: number | null;
  offset: number | null;
}

export const LAST_OPENED_KEY = 'hunter.lastOpenedCompanies';
const LAST_OPENED_LIMIT = 50;

const toNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;

const readLastOpened = () => {
  const entries = new Map<string, LastOpened>();
  try {
    const stored = JSON.parse(sessionStorage.getItem(LAST_OPENED_KEY) ?? '[]');
    (Array.isArray(stored) ? stored : []).forEach((x: unknown) => {
      if (!Array.isArray(x) || typeof x[0] !== 'string') {
        return;
      }
      const value = x[1] as Partial<LastOpened> | string;
      if (typeof value === 'string') {
        // The former format: the id only.
        entries.set(x[0], { id: value, index: null, offset: null });
      } else if (value && typeof value === 'object') {
        entries.set(x[0], {
          id: typeof value.id === 'string' ? value.id : null,
          index: toNumber(value.index),
          offset: toNumber(value.offset),
        });
      }
    });
  } catch {
    // Nothing remembered.
  }
  return entries;
};

/**
 * The companies behind a company page that was opened from a list or the
 * search, for previous/next and the position by absolute index. A source the
 * list or search page left behind is used when it fits the context of the
 * URL; otherwise, after a reload or in a new tab, the first chunk of the
 * context is loaded again (and the chunk of the company, see RowNavigator).
 *
 * The service also keeps the source of the last search, so going back to the
 * search page does not repeat it, and per query the company opened last with
 * its index and the scroll offset of the table, so the way back shows the
 * same row.
 */
@Injectable({ providedIn: 'root' })
export class CompanyNavigationService {
  private companyService = inject(CompanyService);
  private localStorage = inject<Storage>(LOCAL_STORAGE);

  private search: { key: string; source: CompanyListSource<Company> } | null =
    null;
  /** Kept in the session storage, so a reload of the list still finds it. */
  private lastOpened = readLastOpened();

  /** The company opened from the query, at this index of the whole list. */
  public setLastOpened(
    context: CompanyContext,
    companyId: string,
    index: number | null
  ) {
    const key = contextQueryKey(context);
    const before = this.lastOpened.get(key);
    this.remember(key, {
      id: companyId,
      index,
      offset: before?.offset ?? null,
    });
  }

  /** The scroll offset of the table of the query, when the user leaves it. */
  public setScrollOffset(context: CompanyContext, offset: number | null) {
    const key = contextQueryKey(context);
    const before = this.lastOpened.get(key);
    this.remember(key, {
      id: before?.id ?? null,
      index: before?.index ?? null,
      offset,
    });
  }

  public getLastOpened(context: CompanyContext): LastOpened | null {
    return this.lastOpened.get(contextQueryKey(context)) ?? null;
  }

  private remember(key: string, value: LastOpened) {
    this.lastOpened.delete(key);
    this.lastOpened.set(key, value);
    try {
      sessionStorage.setItem(
        LAST_OPENED_KEY,
        JSON.stringify([...this.lastOpened].slice(-LAST_OPENED_LIMIT))
      );
    } catch {
      // Without storage the mark lasts until the next reload.
    }
  }

  /**
   * The source of the search with the filter and sorting of the state. The
   * last one is reused for the same filter and sorting and keeps its loaded
   * rows; it loads again only when it failed, and refreshes its rows when
   * companies changed since.
   */
  public searchSource(state: SearchState): CompanyListSource<Company> {
    const key = JSON.stringify([state.filterValue, state.sort]);
    if (!this.search || this.search.key !== key) {
      // Another filter or sorting: the chunks of the former search go.
      this.search?.source.dropCache();
      const source = new CompanyListSource<Company>(
        (options) => this.companyService.fetchCompanies(options),
        'business_id',
        COMPANY_LIST_PAGE_SIZE,
        {
          cache: this.companyService.companyPages,
          scope: 'search',
          prefetch: true,
        }
      );
      source.setFilter(state.filterValue);
      source.setSort(searchSortDescriptors(state));
      this.search = { key, source };
    }
    const source = this.search.source;
    source.open().catch(() => undefined);
    return source;
  }

  /** The source of a context, with its first chunk loaded. */
  public async sourceFor(
    context: CompanyContext
  ): Promise<CompanyListSource<Company>> {
    if (context.kind === 'search') {
      const state = deserializeSearchState(context.state) ?? {
        filterValue: null,
        sort: null,
      };
      const source = this.searchSource(state);
      await source.whenLoaded().catch(() => undefined);
      return source;
    }

    const filter = toListRequestFilter(contextFilter(context));
    let source = this.companyService.companyListSourceOf(context.listId);
    if (!source || !sameJson(source.currentFilter, filter)) {
      source = this.companyService.getCompanyListSource(context.listId);
      source.setFilter(filter);
      source.setSort(toSortDescriptors(await this.listColumns(context.listId)));
    }
    await source.open().catch(() => undefined);
    return source;
  }

  /** The sorted columns of a list as the list page shows them. */
  private async listColumns(listId: number) {
    const list = await firstValueFrom(
      this.companyService.allLists$.pipe(
        map((lists) => lists.find((x) => x.id === listId) ?? null)
      )
    );
    const columns = list ? savedListColumns(list, this.localStorage) : null;
    return applyColumnState(columnDefinitions(), columns).columns;
  }
}
